import { apiFetch, authHeader } from '../../shared/api/httpClient.ts';
import { compressImage } from '../support/feedback/compressImage.ts';
import type { ProofStatus } from '../../shared/wallet/unpaidDeposits.ts';

export type DepositProof = {
  id: number;
  referenceCode: string | null;
  contentType: string | null;
  status: ProofStatus;
  reviewNote: string;
  createdAt: number;
};

export const PROOF_FILE_MAX_BYTES = 10 * 1024 * 1024;

export const PROOF_CODE_LENGTH = 22;

export function normalizeProofCode(raw: string): string {
  return String(raw || '').replace(/\s+/g, '').toUpperCase();
}

/** What the input field keeps: normalized and cut at 22 characters, so a paste with extra characters cannot overflow. */
export function clampProofCode(raw: string): string {
  return normalizeProofCode(raw).slice(0, PROOF_CODE_LENGTH);
}

export function validProofCode(code: string): boolean {
  return /^[A-Z0-9]{22}$/.test(normalizeProofCode(code));
}

const endpoint = (publicId: string) => `/api/wallet/deposits/${encodeURIComponent(publicId)}/proofs`;

export async function fetchDepositProofs(token: string, publicId: string): Promise<DepositProof[]> {
  const response = await apiFetch<{ items?: DepositProof[] }>(endpoint(publicId), { headers: authHeader(token), cache: 'no-store' });
  return response.items || [];
}

export async function submitDepositProof(
  token: string,
  publicId: string,
  input: { requestId: string; referenceCode: string; file: Blob | null },
): Promise<DepositProof[]> {
  const form = new FormData();
  form.set('requestId', input.requestId);
  form.set('referenceCode', normalizeProofCode(input.referenceCode));
  if (input.file) form.set('file', input.file, input.file.type === 'application/pdf' ? 'comprobante.pdf' : 'comprobante.jpg');
  const response = await apiFetch<{ items?: DepositProof[] }>(endpoint(publicId), { method: 'POST', headers: authHeader(token), body: form });
  return response.items || [];
}

export async function fetchDepositProofFile(token: string, publicId: string, proofId: number): Promise<Blob> {
  const response = await fetch(`${endpoint(publicId)}/${proofId}/file`, { headers: authHeader(token), cache: 'no-store' });
  if (!response.ok) throw new Error(`HTTP_${response.status}`);
  return response.blob();
}

/** Size-check, then compress images (PDF passes through). Throws PROOF_FILE_SIZE / PROOF_FILE_TYPE. */
export async function prepareProofFile(file: File): Promise<Blob> {
  if (file.size > PROOF_FILE_MAX_BYTES) throw new Error('PROOF_FILE_SIZE');
  if (file.type === 'application/pdf') return file;
  try {
    return (await compressImage(file)).blob;
  } catch {
    throw new Error('PROOF_FILE_TYPE');
  }
}

export type ProofErrorKey = 'unpaidErrInvalid' | 'unpaidErrUsed' | 'unpaidErrFile' | 'unpaidErrLimit' | 'unpaidErrClosed' | 'unpaidErrGeneric';

export function proofErrorKey(reason: string): ProofErrorKey {
  switch (reason) {
    case 'PROOF_CODE_INVALID': return 'unpaidErrInvalid';
    case 'PROOF_CODE_USED': return 'unpaidErrUsed';
    case 'PROOF_FILE_TYPE':
    case 'PROOF_FILE_SIZE':
    case 'HTTP_413': return 'unpaidErrFile';
    case 'PROOF_LIMIT': return 'unpaidErrLimit';
    case 'PROOF_ORDER_CLOSED': return 'unpaidErrClosed';
    default: return 'unpaidErrGeneric';
  }
}
